import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { Ripple } from './ripple';
import { SweepText, type SweepTextVariant } from './sweep-text';

import { cn } from '@/lib/utils';

export interface RippleStatusProps {
  /** The status text to display. Each caller supplies its own. */
  text: string;
  className?: string;
  variant?: SweepTextVariant;
}

/**
 * Presentational loading status: an animated ripple glyph alongside shimmer
 * status text that cross-fades whenever the text changes. Purely presentational
 * — callers populate `text` with their own logic.
 */
export function RippleStatus({ text, className, variant = 'dark-subtle' }: RippleStatusProps) {
  return (
    <div className={cn('inline-flex items-start gap-1.5 py-1', className)}>
      <span className="flex h-6 shrink-0 items-center">
        <Ripple className="size-4" />
      </span>
      {/* Grid stacks elements in the same cell so enter/exit overlap,
          producing a cross-fade. mode="wait" (sequential) is intentionally
          absent — without it both animations play simultaneously, which is
          what we want here. */}
      <span className="relative grid min-w-0 overflow-hidden">
        <AnimatePresence initial={false}>
          <motion.span
            key={text}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="col-start-1 row-start-1"
          >
            <SweepText variant={variant}>{text}</SweepText>
          </motion.span>
        </AnimatePresence>
      </span>
    </div>
  );
}
